import React, { useMemo, useState } from "react";
import { User } from "../../data/User";
import TextInput from "../../common/TextInput";

export interface UserListProps {
  list: User[];
  onEdit: (session: User) => void;
}

/** Case-insensitive, null-safe text for matching and sorting. */
function norm(value: string | null | undefined): string {
  return (value ?? "").toLowerCase();
}

/** Sort by house, then last name, then first name. */
export function compareUsers(a: User, b: User): number {
  return norm(a.house).localeCompare(norm(b.house)) || norm(a.lastname).localeCompare(norm(b.lastname)) || norm(a.firstname).localeCompare(norm(b.firstname));
}

/** True when the user matches the search text on name, email or parish. */
export function userMatchesSearch(user: User, searchText: string): boolean {
  const str = searchText.trim().toLowerCase();
  if (str === "") {
    return true;
  }
  return norm(user.firstname).includes(str) || norm(user.lastname).includes(str) || norm(user.preferredName).includes(str) || norm(user.email).includes(str) || norm(user.parish).includes(str);
}

function UserList({ list, onEdit }: UserListProps) {
  const [searchText, setSearchText] = useState("");

  // derived, so the results always reflect the current list and search text:
  // filtering happens as you type, and a list refresh can't desync the view
  const users = useMemo(() => [...list].sort(compareUsers).filter((u) => userMatchesSearch(u, searchText)), [list, searchText]);

  return (
    <>
      <div className="row align-items-end">
        <div className="col-md-5">
          <TextInput
            label="Search"
            name="Search"
            error=""
            value={searchText}
            onChange={setSearchText}
            type="text"
            helpText=""
            placeholder="name, email or parish"
          />
        </div>
        <div className="col-md-7 d-flex align-items-center gap-3 pb-2">
          <button
            className="btn btn-light"
            disabled={searchText === ""}
            onClick={() => setSearchText("")}
          >
            Clear
          </button>
          <span className="text-muted">
            Showing <span className="sl-num">{users.length}</span> of <span className="sl-num">{list.length}</span> users
          </span>
        </div>
      </div>
      <div className="table-responsive">
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>House</th>
              <th>Roles</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.map((item) => {
              return (
                <tr key={item.userId} title={item.alternateId} className={item.enabled ? "" : "text-muted"}>
                  <td>
                    {item.enabled ? (
                      <>
                        {item.lastname}, {item.preferredName ? item.preferredName : item.firstname}
                      </>
                    ) : (
                      <del>
                        {item.lastname}, {item.preferredName ? item.preferredName : item.firstname}
                      </del>
                    )}
                  </td>
                  <td>{item.enabled ? <>{item.email}</> : <del>{item.email}</del>}</td>
                  <td>{item.house}</td>
                  <td>{item.userRoles}</td>
                  <td>
                    <button className="btn btn-light btn-sm" onClick={() => onEdit(item)}>
                      Edit
                    </button>
                  </td>
                </tr>
              );
            })}
            {users.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-muted">
                  {list.length === 0 ? "No Users found" : "No users match your search"}
                </td>
              </tr>
            ) : (
              <></>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default UserList;
